/**
 * The props catalogue — the built-in kits (kits.ts) with what Prop Studio has
 * said about them on top: where each prop can go (`places`), which spaces it is
 * staged in (`spaces`), and props made in Prop Studio (`added`, with their
 * name and size). It lives in the props library's `catalogue`
 * (scripts/blender/props/library.json, served by the Prop Studio back ends),
 * so it is data, not code: a prop can be put on a shelf as well as the floor,
 * moved into another space, or made from scratch, without a release.
 *
 * Without a catalogue, everything is exactly as kits.ts says.
 */

import { SlatWallAccessoryKind, accessoryDef } from '../../data/slatwallAccessories';
import { Placement, PropDef, PropKind, PropSize, STAGING_KITS, StagingKit } from './kits';


/** What the catalogue says about one prop (every field optional; an `added` prop has a label and size). */
export interface CatalogueEntry {
  label?: string;
  size?: PropSize;
  places?: Placement[];
  spaces?: string[];
  added?: boolean;
  drop?: number;
  count?: number;
  /** The count holds whatever the staging density (kits.ts `fixed`). */
  fixed?: boolean;
  /** Out of the staging pool, everything else kept (kits.ts `disabled`). */
  disabled?: boolean;
  /** When it is placed: lower first; below 30 a main piece, placed once at any density (kits.ts `order`). */
  order?: number;
  /** Floor props: against a wall, out in the room, either, in a corner, or in line with a garage door (kits.ts `anchor`). */
  anchor?: 'wall' | 'free' | 'any' | 'corner' | 'bay' | 'lawn' | 'window';
  /** Floor props: stands in front of this prop, facing it, when it is in the room (kits.ts `faces`); '' / absent: faces the room. */
  faces?: string;
  /** How far in front of that prop it stands, feet (kits.ts `facesGap`; absent: a hand's gap). */
  facesGap?: number;
  /** Floor props against a wall: at the middle of its wall only (kits.ts `centred`). */
  centred?: boolean;
  /** Floor props: stands right beside this prop on the same wall (kits.ts `besides`); '' / absent: on its own. */
  besides?: string;
  /** The gap to that prop, feet (kits.ts `besidesGap`; absent: a hand's gap). */
  besidesGap?: number;
  /** Floor props out in the room: may stand at any angle, facing the middle (kits.ts `askew`). */
  askew?: boolean;
  /** Bay props: which way it faces, and how much of it is in the room (kits.ts `heading`, `inside`). */
  heading?: 'in' | 'out' | 'any';
  inside?: number;
  /** Floor props: the room kept clear in front (feet). */
  clearance?: number;
  /** The room kept clear either side (feet): nothing else, and no wall end or corner, this close. */
  clearSide?: number;
  /** Floor props: how far off the wall it stands (feet). */
  clearBack?: number;
  /** Wall props: the height the prop is held at (feet above the floor). */
  mountHeight?: number;
  /** Held props: the hold point along the wall from the box's centre (feet, + to the right). */
  holdX?: number;
  /** Held props: the hold point out from the wall (feet). */
  holdZ?: number;
}
export type Catalogue = Record<string, CatalogueEntry>;

/** The SlatWall accessory each wall placement is. */
export const PLACEMENT_ACCESSORY: Partial<Record<Placement, SlatWallAccessoryKind>> = {
  shelf: 'shelf-24',
  basket: 'basket-18',
  'hook-single': 'hook-single',
  'hook-double': 'hook-double',
  'hook-bike': 'hook-bike',
  'hook-hose': 'hook-hose',
};

/** The placement an accessory is. */
export function placementOf(kind: SlatWallAccessoryKind): Placement {
  const hit = (Object.keys(PLACEMENT_ACCESSORY) as Placement[]).find((p) => PLACEMENT_ACCESSORY[p] === kind);
  return hit ?? 'hook-single';
}

export const isAccessoryPlace = (p: Placement): boolean => PLACEMENT_ACCESSORY[p] !== undefined;

/**
 * Where a prop can go, best first: its `places` when it has them, else what
 * the kit says — the floor for a floor prop, the accessories a hung prop hangs
 * on. A wall prop (a towel bar, wall art) is on the wall and nowhere else.
 */
export function placesOf(def: PropDef): Placement[] {
  if (def.kind === 'wall') return ['wall'];
  if (def.places && def.places.length) return def.places;
  if (def.kind === 'floor') return ['floor'];
  if (def.kind === 'ceiling') return ['ceiling'];
  if (def.kind === 'yard') return ['yard'];
  if (def.kind === 'slatwall') return (def.hangsOn ?? []).map(placementOf);
  return [];
}

/**
 * How far below the point that holds it a prop hangs on an accessory: nothing
 * on a shelf or in a basket (it sits on or in it); on a hook, its own `drop`,
 * or most of its height when it has none (a prop made for the floor, hung up).
 */
export function dropOn(def: PropDef, kind: SlatWallAccessoryKind): number {
  const hold = accessoryDef(kind).hold;
  if (hold !== 'hang') return 0;
  return def.drop && def.drop > 0 ? def.drop : def.size.h * 0.85;
}

const kindFor = (places: Placement[]): PropKind => {
  const first = places[0];
  if (first === 'wall') return 'wall';
  if (first === 'floor') return 'floor';
  if (first === 'ceiling') return 'ceiling';
  if (first === 'yard') return 'yard';
  return 'slatwall';
};

/**
 * A prop with its catalogue entry applied. A built-in wall prop (the towel
 * bar) keeps its kind whatever the entry says; any other prop put "on the
 * wall" becomes a wall prop, held at its mount height (stage.ts placeWall).
 */
export function withEntry(def: PropDef, e: CatalogueEntry | undefined): PropDef {
  if (!e) return def;
  const out: PropDef = { ...def };
  if (e.label) out.label = e.label;
  if (e.size) out.size = { ...e.size };
  if (e.drop !== undefined) out.drop = e.drop;
  if (e.count !== undefined) out.count = e.count;
  if (e.fixed !== undefined) out.fixed = e.fixed;
  if (e.disabled !== undefined) out.disabled = e.disabled;
  if (e.order !== undefined) out.order = e.order;
  if (e.anchor !== undefined) out.anchor = e.anchor;
  if (e.faces !== undefined) out.faces = e.faces || undefined;
  if (e.facesGap !== undefined) out.facesGap = e.facesGap;
  if (e.centred !== undefined) out.centred = e.centred;
  if (e.besides !== undefined) out.besides = e.besides || undefined;
  if (e.besidesGap !== undefined) out.besidesGap = e.besidesGap;
  if (e.askew !== undefined) out.askew = e.askew;
  if (e.heading !== undefined) out.heading = e.heading;
  if (e.inside !== undefined) out.inside = e.inside;
  if (e.clearance !== undefined) out.clearance = e.clearance;
  if (e.clearSide !== undefined) out.clearSide = e.clearSide;
  if (e.clearBack !== undefined) out.clearBack = e.clearBack;
  if (e.mountHeight !== undefined) out.mountHeight = e.mountHeight;
  if (e.holdX !== undefined) out.holdX = e.holdX;
  if (e.holdZ !== undefined) out.holdZ = e.holdZ;
  if (e.places && e.places.length && def.kind !== 'wall') {
    out.places = [...e.places];
    out.kind = kindFor(e.places);
    const hangs = e.places.map((p) => PLACEMENT_ACCESSORY[p]).filter((k): k is SlatWallAccessoryKind => !!k);
    out.hangsOn = hangs.length ? hangs : undefined;
  }
  return out;
}

/** A prop made in Prop Studio, as a kit prop. */
export function madeProp(id: string, e: CatalogueEntry): PropDef {
  const places = e.places && e.places.length ? e.places : ['floor' as Placement];
  return withEntry({
    id,
    label: e.label ?? id,
    kind: kindFor(places),
    size: e.size ?? { w: 1, d: 1, h: 1 },
    anchor: places[0] === 'yard' ? 'lawn' : 'wall',
    clearance: 0.5,
    order: 80,
  }, { ...e, places });
}

/** Every built-in prop by id (its first definition), and the spaces whose kits have it. */
function builtIns(): { defs: Map<string, PropDef>; spaces: Map<string, string[]> } {
  const defs = new Map<string, PropDef>();
  const spaces = new Map<string, string[]>();
  for (const [space, kit] of Object.entries(STAGING_KITS)) {
    for (const p of kit.props) {
      if (!defs.has(p.id)) defs.set(p.id, p);
      spaces.set(p.id, [...(spaces.get(p.id) ?? []), space]);
    }
  }
  return { defs, spaces };
}
const BUILT_IN = builtIns();

/** The spaces a prop is staged in: its entry's, else the kits that have it. */
export function spacesOf(id: string, catalogue?: Catalogue): string[] {
  return catalogue?.[id]?.spaces ?? BUILT_IN.spaces.get(id) ?? [];
}

/**
 * The kits with the catalogue applied: each space's own props (less any whose
 * entry no longer lists the space), with their entries, plus every prop whose
 * entry adds the space — a built-in from another kit, or a prop made in Prop Studio.
 */
export function kitsWith(catalogue?: Catalogue): Record<string, StagingKit> {
  if (!catalogue || Object.keys(catalogue).length === 0) return STAGING_KITS;
  const out: Record<string, StagingKit> = {};
  for (const [space, kit] of Object.entries(STAGING_KITS)) {
    const props: PropDef[] = [];
    const seen = new Set<string>();
    for (const p of kit.props) {
      const e = catalogue[p.id];
      if (e?.spaces && !e.spaces.includes(space)) continue;
      props.push(withEntry(p, e));
      seen.add(p.id);
    }
    for (const [id, e] of Object.entries(catalogue)) {
      if (seen.has(id) || !e.spaces?.includes(space)) continue;
      const base = BUILT_IN.defs.get(id);
      if (base) props.push(withEntry(base, e));
      else if (e.added) props.push(madeProp(id, e));
    }
    out[space] = { ...kit, props };
  }
  return out;
}

/** The kit for a space type, the catalogue applied ('other' when the type has none). */
export function kitWith(spaceType: string | undefined | null, catalogue?: Catalogue): StagingKit {
  const kits = kitsWith(catalogue);
  return kits[spaceType ?? ''] ?? kits.other;
}

/** One prop by id, the catalogue applied: a built-in, or one made in Prop Studio. */
export function propById(id: string, catalogue?: Catalogue): PropDef | undefined {
  const base = BUILT_IN.defs.get(id);
  const e = catalogue?.[id];
  if (base) return withEntry(base, e);
  return e?.added ? madeProp(id, e) : undefined;
}

/** A new prop's id from its name: lower case, a-z 0-9 and dashes, not one already taken. */
export function newPropId(label: string, taken: (id: string) => boolean): string {
  const base = label.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'prop';
  if (!taken(base) && !BUILT_IN.defs.has(base)) return base;
  for (let n = 2; n < 1000; n++) {
    const id = `${base}-${n}`;
    if (!taken(id) && !BUILT_IN.defs.has(id)) return id;
  }
  return `${base}-${Date.now() % 100000}`;
}

/**
 * A prop as it is when placed one particular way (Prop Studio previews each):
 * on the floor, hung from the ceiling (the whole of it below the point it is
 * held at), or on one accessory (hanging its drop below a hook, sitting on a
 * shelf or in a basket).
 */
export function asPlaced(def: PropDef, place: Placement): PropDef {
  if (place === 'floor') return { ...def, kind: 'floor' };
  if (place === 'wall') return { ...def, kind: 'wall' };
  if (place === 'ceiling') return { ...def, kind: 'ceiling', drop: def.size.h };
  if (place === 'yard') return { ...def, kind: 'yard' };
  const acc = PLACEMENT_ACCESSORY[place];
  if (!acc) return def;
  return { ...def, kind: 'slatwall', hangsOn: [acc], drop: dropOn(def, acc) };
}

/**
 * A prop's places after a box is ticked or cleared: what was there keeps its
 * order (best first); a newly ticked accessory goes after the last accessory,
 * the floor or the ceiling at the end. At least one place always stays. The
 * wall is on its own: ticking it clears the rest, ticking anything else clears it.
 */
export function togglePlace(places: Placement[], place: Placement, on: boolean): Placement[] {
  if (!on) {
    const next = places.filter((p) => p !== place);
    return next.length ? next : places;
  }
  if (places.includes(place)) return places;
  if (place === 'wall') return ['wall'];
  if (places.includes('wall')) return [place];
  if (!isAccessoryPlace(place)) return [...places, place];
  let lastAcc = -1;
  places.forEach((p, i) => { if (isAccessoryPlace(p)) lastAcc = i; });
  const next = [...places];
  next.splice(lastAcc + 1, 0, place);
  return next;
}

/**
 * The fixtures Prop Studio can give a model to, that staging never places:
 * the garage door opener's motor, hung where the garage door's hardware puts
 * it (scripts/blender/garage_hardware.py) -- its top-centre at the top-centre
 * of the motor's box, its front toward the door. Without a model the renderer
 * builds its own.
 */
export const FIXTURES: PropDef[] = [
  { id: 'garage-opener', label: 'Garage door opener (motor)', kind: 'ceiling', size: { w: 1.3, d: 1.5, h: 0.6 } },
];
